import { SettingIcon } from '@/components/SettingIcon';
import { TicketPreview } from '@/features/print/components/TicketPreview';
import { usePrintSettings, useUpdatePrintSettings } from '@/features/print/hooks/usePrint';
import { useT } from '@/i18n';
import type {
  PrintDateTimeFormat,
  PrintItemLabelMode,
  PrintTextSize,
} from '@restaurant/api-client';
import {
  Block,
  BlockFooter,
  BlockTitle,
  List,
  ListItem,
  Navbar,
  Page,
  Preloader,
  f7,
} from 'framework7-react';
import { ToggleSwitch } from '@/components/ToggleSwitch';

const DATE_TIME_FORMATS: { value: PrintDateTimeFormat; label: string }[] = [
  { value: 'DMY_SLASH', label: 'DD/MM/YYYY' },
  { value: 'DMY_DOT', label: 'DD.MM.YYYY' },
  { value: 'YMD_DASH', label: 'YYYY-MM-DD' },
  { value: 'MDY_SLASH', label: 'MM/DD/YYYY' },
];

// "What to print" — the per-restaurant auto-print policy. Two independent
// toggles: online orders (default ON, the kitchen relies on them) and confirmed
// reservations (default OFF, opt-in), plus the ticket text size (applies to
// every ticket type). Mirrors the table-ordering settings page chrome and its
// per-change toast idiom. Dine-in fired rounds are governed on the
// table-ordering settings page, not here.
export default function PrintBehaviourPage() {
  const t = useT();
  const tp = t.print;
  const { data: settings, isLoading } = usePrintSettings();
  const update = useUpdatePrintSettings();

  const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });

  if (isLoading || !settings) {
    return (
      <Page name="print-behaviour">
        <Navbar title={tp.behaviourPageTitle} backLink={t.back} />
        <Block style={{ textAlign: 'center', paddingTop: 48 }}>
          <Preloader />
        </Block>
      </Page>
    );
  }

  const save = (body: Parameters<typeof update.mutate>[0], done: string) =>
    update.mutate(body, { onSuccess: () => toast(done) });

  // All three stay visible: the wording is what explains the choice.
  const modeRow = (mode: PrintItemLabelMode, label: string) => (
    <ListItem
      key={mode}
      title={label}
      link="#"
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();
        if (settings.itemLabelMode === mode) return;
        save({ itemLabelMode: mode }, tp.itemLabelUpdated);
      }}
    >
      {settings.itemLabelMode === mode && (
        <i
          slot="after"
          className="f7-icons"
          style={{ color: 'var(--f7-theme-color)', fontSize: 20 }}
        >
          checkmark_alt
        </i>
      )}
    </ListItem>
  );

  return (
    <Page name="print-behaviour">
      <Navbar title={tp.behaviourPageTitle} backLink={t.back} />

      <BlockFooter style={{ marginTop: 24 }}>{tp.behaviourPageFooter}</BlockFooter>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListItem title={tp.printOnlineOrdersLabel}>
          <div slot="media">
            <SettingIcon icon="bag" color="#34c759" />
          </div>
          <ToggleSwitch
            slot="after"
            checked={settings.printOnlineOrders}
            onChange={(v) => save({ printOnlineOrders: v }, tp.onlineOrdersUpdated)}
          />
        </ListItem>
      </List>
      <BlockFooter>{tp.printOnlineOrdersFooter}</BlockFooter>

      <List strongIos strongMd insetIos insetMd dividersIos dividersMd style={{ marginTop: 24 }}>
        <ListItem title={tp.printReservationsLabel}>
          <div slot="media">
            <SettingIcon icon="calendar" color="#5856d6" />
          </div>
          <ToggleSwitch
            slot="after"
            checked={settings.printReservations}
            onChange={(v) => save({ printReservations: v }, tp.reservationsUpdated)}
          />
        </ListItem>
      </List>
      <BlockFooter>{tp.printReservationsFooter}</BlockFooter>

      {/* Not a "what to print" question but a "how it looks" one, so it sits in
          its own section. Two named sizes only — the font/scale/line-spacing
          behind each lives in the backend's ePOS builder, not in the operator's
          head. */}
      <BlockTitle style={{ marginTop: 32 }}>{tp.textSizeTitle}</BlockTitle>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListItem
          title={tp.textSizeLabel}
          smartSelect
          smartSelectParams={{ openIn: 'popover', setValueText: true }}
        >
          <div slot="media">
            <SettingIcon icon="textformat.size" color="#ff9500" />
          </div>
          <select
            value={settings.textSize}
            onChange={(e) =>
              save(
                { textSize: (e.target as HTMLSelectElement).value as PrintTextSize },
                tp.textSizeUpdated,
              )
            }
          >
            <option value="MEDIUM">{tp.textSizeMedium}</option>
            <option value="LARGE">{tp.textSizeLarge}</option>
          </select>
        </ListItem>
      </List>
      <BlockFooter>{tp.textSizeFooter}</BlockFooter>

      <List strongIos insetIos dividersIos style={{ marginTop: 24 }}>
        <ListItem title={tp.itemCategoryLabel}>
          <div slot="media">
            <SettingIcon icon="tag" color="#af52de" />
          </div>
          <ToggleSwitch
            slot="after"
            checked={settings.printItemCategory}
            onChange={(v) => save({ printItemCategory: v }, tp.itemCategoryUpdated)}
          />
        </ListItem>
      </List>
      <BlockFooter>{tp.itemCategoryFooter}</BlockFooter>

      <BlockTitle style={{ marginTop: 32 }}>{tp.itemLabelTitle}</BlockTitle>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        {modeRow('NAME', tp.itemLabelName)}
        {modeRow('CODE_AND_NAME', tp.itemLabelCodeAndName)}
        {modeRow('CODE_ONLY', tp.itemLabelCodeOnly)}
      </List>
      <BlockFooter>{tp.itemLabelFooter}</BlockFooter>

      <List strongIos strongMd insetIos insetMd dividersIos dividersMd style={{ marginTop: 24 }}>
        <ListItem
          title={tp.dateTimeFormatLabel}
          smartSelect
          smartSelectParams={{ openIn: 'popover', setValueText: true }}
        >
          <div slot="media">
            <SettingIcon icon="clock" color="#007aff" />
          </div>
          <select
            value={settings.dateTimeFormat}
            onChange={(e) => {
              const next = DATE_TIME_FORMATS.find((f) => f.value === e.target.value);
              if (next) save({ dateTimeFormat: next.value }, tp.dateTimeFormatUpdated);
            }}
          >
            {DATE_TIME_FORMATS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </ListItem>
      </List>
      <BlockFooter>{tp.dateTimeFormatFooter}</BlockFooter>

      {/* Rendered server-side from the same ePOS builder the printer gets, so
          the strip below is what the paper will say. */}
      <BlockTitle style={{ marginTop: 32 }}>{tp.previewTitle}</BlockTitle>
      <Block>
        <TicketPreview kind="ONLINE_ORDER" />
      </Block>
      <BlockFooter>{tp.previewFooter}</BlockFooter>
    </Page>
  );
}
